import { slideText, songSlides } from '../lib/slides';

// Shared contract between the console (ChurchApp) and the phone remote
// (/remote). The two talk over a Supabase Realtime Broadcast channel named
// after a random pairing code, so the code is the only secret.

export const REMOTE_PATH = '/remote';
export const OBS_PATH = '/obs';

// No 0/O/1/I/L so a code read off a screen cannot be mistyped.
const ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
const CODE_LENGTH = 8;

export const MSG = {
  HELLO: 'hello', // phone -> console: "I just opened, send the state"
  PING: 'ping',   // phone -> console: "still here"
  STATE: 'state', // console -> phone: a snapshot (see buildSnapshot)
  CMD: 'cmd',     // phone -> console: { cmd, index? }
  OUTPUT: 'output', // console -> /obs: { isLive, content, currentLine, style }
};

export const CMD = {
  NEXT: 'next',           // stage the next run-sheet item
  PREV: 'prev',           // stage the previous item
  GOTO: 'goto',           // stage the item at `index`
  SEND: 'send',           // send the staged item live
  CLEAR: 'clear',         // clear the live display
  LINE_NEXT: 'lineNext',  // next lyric line
  LINE_PREV: 'linePrev',  // previous lyric line
};

export const HEARTBEAT_MS = 10000;      // both sides announce themselves this often
export const STALE_AFTER_MS = 25000;    // a side silent for this long counts as gone
const MAX_ITEMS = 100;

export function generateCode(random = defaultRandom) {
  let code = '';
  for (let i = 0; i < CODE_LENGTH; i++) code += ALPHABET[random(ALPHABET.length)];
  return code;
}

function defaultRandom(n) {
  const buf = new Uint32Array(1);
  // Reject values that would bias the result towards the first characters.
  const limit = Math.floor(0x100000000 / n) * n;
  do { crypto.getRandomValues(buf); } while (buf[0] >= limit);
  return buf[0] % n;
}

// "k7m2-qx9p" / " K7M2 QX9P " -> "K7M2QX9P"
export function normalizeCode(input = '') {
  return String(input).toUpperCase().replace(/[^A-Z0-9]/g, '');
}

export function isValidCode(input) {
  const code = normalizeCode(input);
  return code.length === CODE_LENGTH && [...code].every(c => ALPHABET.includes(c));
}

export function formatCode(code) {
  const c = normalizeCode(code);
  return c.length > 4 ? `${c.slice(0, 4)}-${c.slice(4)}` : c;
}

export function channelName(code) {
  return `sleekhear-remote-${normalizeCode(code)}`;
}

// The livestream output (/obs) listens on its own channel so phones are not sent its traffic.
export function obsChannelName(code) {
  return `sleekhear-obs-${normalizeCode(code)}`;
}

export function obsUrl(origin, code) {
  return `${origin}${OBS_PATH}?c=${normalizeCode(code)}`;
}

export function remoteUrl(origin, code) {
  return `${origin}${REMOTE_PATH}?c=${normalizeCode(code)}`;
}

// A phone cannot reach "localhost" (that is the phone itself).
export function isLocalHost(hostname = '') {
  return /^(localhost|127\.\d+\.\d+\.\d+|\[?::1\]?|0\.0\.0\.0)$/i.test(hostname);
}

// "192.168.1.57:3000" -> "http://192.168.1.57:3000"; trailing slashes and paths dropped.
// Returns '' when it is not a usable address.
export function normalizeBaseUrl(input = '') {
  const text = String(input).trim();
  if (!text) return '';
  try {
    const url = new URL(/^https?:\/\//i.test(text) ? text : `http://${text}`);
    return url.hostname ? url.origin : '';
  } catch {
    return '';
  }
}

export function isValidCommand(msg) {
  if (!msg || typeof msg.cmd !== 'string') return false;
  if (msg.cmd === CMD.GOTO) return Number.isInteger(msg.index) && msg.index >= 0;
  return Object.values(CMD).includes(msg.cmd);
}

const itemLabel = (item) => (item.kind === 'scripture' ? item.reference : item.title) || '';

// What a screen shows as the name of staged / live content.
export function contentLabel(content) {
  if (!content) return '';
  if (content.type === 'scripture') return content.reference || '';
  if (content.type === 'song') return content.title || '';
  return '';
}

/**
 * The small, display-only picture of the console sent to the phone: run-sheet
 * labels, where we are, what is staged and live, and the current lyric line.
 */
export function buildSnapshot({ plan, items, currentIndex, stagedContent, displayContent, isLive, currentLine, slides }) {
  // The phone steps through slides: a song's lines/stanzas, or the parts of a long passage.
  let line = null;
  const list = Array.isArray(slides) ? slides : (displayContent && displayContent.type === 'song' ? songSlides(displayContent.content, 1) : []);
  if (displayContent && (displayContent.type === 'song' || list.length > 1)) {
    line = { index: currentLine, total: list.length, text: slideText(displayContent, slides, currentLine) };
  }
  return {
    planName: (plan && plan.name) || '',
    items: items.slice(0, MAX_ITEMS).map(item => ({ kind: item.kind, label: itemLabel(item) })),
    currentIndex,
    staged: { type: stagedContent ? stagedContent.type : 'welcome', label: contentLabel(stagedContent) },
    live: { type: displayContent ? displayContent.type : 'welcome', label: contentLabel(displayContent) },
    isLive: !!isLive,
    line,
  };
}
